import React, { useCallback, useMemo, useEffect } from 'react';
import { shallowEqual } from 'react-redux';
import { Column } from 'react-table';

import logError from 'utils/logError';

import { ObjectType, AlgoliaAlert, AlertId } from '../../types/alerts';
import { getMultiValueColumnFieldSort, getColumnFieldFuzzyFilter } from '../../utils/tables';
import Table from '../Table';
import TableColumn from '../Table/TableColumn';
import { RowData } from './types';
import AlertButton from './AlertButton';
import TrailNamesColumn from './TrailNamesColumn';
import ActionsColumn from './ActionsColumn';
import TrailNameFilter from '../filters/TrailNameFilter';
import DateColumn from './DateColumn';
import AlertsColumn from './AlertColumn';
import { useSelector, useDispatch } from '../../redux/hooks';
import { selectStatus as selectAlertsStatus, fetchGroupsAlerts } from '../../redux/slices/alerts/alerts';
import { selectStatus as selectTrailsStatus, fetchGroupsTrails } from '../../redux/slices/trails/trails';
import { selectStatus as selectAreasStatus, fetchGroupsAreas } from '../../redux/slices/areas/areas';

const columns: Column<RowData>[] = [
  {
    Header: 'Alert name',
    accessor: 'alertName',
    Cell: TableColumn,
    width: 200
  },
  {
    Header: 'Property',
    accessor: 'areaName',
    Cell: TableColumn,
    width: 150
  },
  {
    Header: 'Trails',
    accessor: 'trailNames',
    filter: getColumnFieldFuzzyFilter('trailNames'),
    sortType: getMultiValueColumnFieldSort('trailNames'),
    Cell: TrailNamesColumn,
    Filter: TrailNameFilter,
    width: 150
  },
  {
    Header: 'Start date',
    accessor: 'startDate',
    Cell: DateColumn,
    width: 80
  },
  {
    Header: 'End date',
    accessor: 'endDate',
    Cell: DateColumn,
    width: 80
  },
  {
    Header: 'Alert',
    accessor: 'alert',
    disableSortBy: true,
    Cell: AlertsColumn,
    width: 150
  },
  {
    Header: 'Actions',
    accessor: 'actions',
    Cell: ActionsColumn,
    disableSortBy: true,
    width: 80
  }
];

const AlertsTable = (): JSX.Element => {
  const alertsDrawerEnabled = useSelector(state => state.enabledFeatures.alertsDrawer);
  const { groups, primaryAlgoliaIndexId, alertsAlgoliaIndexId } = useSelector(
    ({ authState }) => ({
      groups: authState.groups,
      primaryAlgoliaIndexId: authState.primaryAlgoliaIndexId,
      alertsAlgoliaIndexId: authState.alertsAlgoliaIndexId
    }),
    shallowEqual
  );
  const { alerts, started: alertsStarted, loading: alertsLoading } = useSelector(selectAlertsStatus, shallowEqual);
  const { trails, started: trailsStarted, loading: trailsLoading } = useSelector(selectTrailsStatus, shallowEqual);
  const { areas, started: areasStarted, loading: areasLoading } = useSelector(selectAreasStatus, shallowEqual);
  const started = alertsStarted && trailsStarted && areasStarted;
  const loading = alertsLoading || trailsLoading || areasLoading;
  const dispatch = useDispatch();
  const reload = useCallback(() => {
    if (groups.length !== 0) {
      dispatch(fetchGroupsAlerts({ indexId: alertsAlgoliaIndexId, groups }));
      dispatch(fetchGroupsTrails({ indexId: primaryAlgoliaIndexId, groups }));
      dispatch(fetchGroupsAreas({ indexId: primaryAlgoliaIndexId, groups }));
    }
  }, [groups, primaryAlgoliaIndexId, alertsAlgoliaIndexId, dispatch]);

  useEffect(() => {
    reload();
  }, [reload]);

  const alertTrailNames = useMemo(() => {
    const result: Record<AlertId, string[]> = {};
    Object.values(trails).forEach(trail => {
      const alertIds = trail.alert_ids || [];
      alertIds.forEach(alertId => {
        result[alertId] ||= [];
        result[alertId].push(trail.name);
      });
    });

    return result;
  }, [trails]);

  const getAlertRow = useCallback(
    (alert: AlgoliaAlert): RowData => {
      const { ID, object_type, object_id, name, start_date, end_date } = alert;
      let startDate = null;
      let endDate = null;
      let areaName = '';
      let editUrl = '';

      if (start_date) {
        startDate = new Date(start_date * 1000);
      }

      if (end_date) {
        endDate = new Date(end_date * 1000);
      }

      if (object_type === ObjectType.Area) {
        const area = areas[object_id];
        if (area) {
          areaName = area.name;
          editUrl = `/admin/bulk_trail_edit/${area.slug}`;
        } else {
          logError(`Area ID ${object_id} from alert ID ${ID} was not found`);
        }
      }

      return {
        alertName: name,
        areaName,
        trailNames: { trailNames: alertTrailNames[ID] || [] },
        startDate,
        endDate,
        alert,
        actions: {
          alert,
          areaId: alert.object_id,
          editUrl
        }
      };
    },
    [alertTrailNames, areas]
  );

  const getAddButton = (): JSX.Element => alertsDrawerEnabled && <AlertButton />;

  const data: RowData[] = useMemo(() => Object.values(alerts).map(alert => getAlertRow(alert)), [alerts, getAlertRow]);

  return <Table columns={columns} data={data} loading={!started || loading} getAddButton={getAddButton} />;
};

export default AlertsTable;
